import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Instagram, Linkedin } from "lucide-react";
import BackButton from "@/components/BackButton";
import PageHeader from "@/components/PageHeader";
import { cleanPolicyText } from "@/utils/policyText";

const SECTIONS = [
  {
    n: "01",
    title: "Introduction",
    body:
      "The Black House (\"we\", \"our\", \"us\") values your privacy. This Privacy Policy explains how we collect, use, store, share and protect your information when you visit theblackhouse.in, create an account, subscribe to updates, place an order, contact customer support or otherwise interact with our brand. This document is an original draft prepared for The Black House and is not copied from any third-party policy.",
  },
  {
    n: "02",
    title: "Information We Collect",
    body:
      "We may collect information you provide directly, including your name, email address, phone number, shipping and billing address, account details, order history, preferences and any information you voluntarily share with us. We may also automatically collect technical information such as IP address, browser type, device information, operating system, pages visited, referring URLs, cookies and analytics data to improve our website.",
  },
  {
    n: "03",
    title: "How We Use Your Information",
    body:
      "We use your information to process orders, deliver products, provide customer support, communicate about orders, improve our products and website, personalise your shopping experience, prevent fraud, comply with legal obligations and send marketing communications where you have consented or where permitted by law.",
  },
  {
    n: "04",
    title: "Cookies & Analytics",
    body:
      "Our website may use cookies and similar technologies for functionality, analytics and performance. You can manage cookies through your browser settings. Disabling cookies may affect certain website features.",
  },
  {
    n: "05",
    title: "Sharing of Information",
    body:
      "We may share information only with trusted service providers such as payment gateways, logistics partners, website hosting providers, analytics providers and customer support partners when necessary to operate our business. We may also disclose information where required by law or to protect our legal rights.",
  },
  {
    n: "06",
    title: "Data Retention",
    body:
      "We retain personal information only for as long as reasonably necessary to fulfil the purposes described in this policy, resolve disputes, enforce agreements and comply with legal obligations.",
  },
  {
    n: "07",
    title: "Your Rights",
    body:
      "Subject to applicable law, you may request access, correction, updating or deletion of your personal information, withdraw marketing consent or request information about how your data is processed by contacting us.",
  },
  {
    n: "08",
    title: "Security",
    body:
      "We implement commercially reasonable technical and organisational safeguards to help protect personal information. However, no online transmission or storage system can be guaranteed completely secure.",
  },
  {
    n: "09",
    title: "Children",
    body:
      "Our website is not intended for children under 18 without parental or guardian supervision.",
  },
  {
    n: "10",
    title: "Policy Updates",
    body:
      "We may revise this Privacy Policy from time to time. Updated versions will be published on theblackhouse.in with a revised effective date.",
  },
];

export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy Policy — THE BLACK HOUSE";
    window.scrollTo(0, 0);
  }, []);

  const ease = [0.16, 1, 0.3, 1];

  return (
    <main
      data-testid="privacy-page"
      className="relative bg-black text-white overflow-x-hidden"
    >
      <PageHeader />
      <BackButton />
      {/* Corner meta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 left-5 md:top-40 md:left-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase"
      >
        Legal · Privacy
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 right-5 md:top-40 md:right-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase text-right"
      >
        Last Updated · 18 · VII · MMXXVI
      </motion.div>

      {/* Header */}
      <header className="max-w-4xl mx-auto px-6 md:px-10 pt-40 md:pt-44 pb-8 md:pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          className="flex flex-col items-center text-center"
        >
          <p className="font-mono-ui text-[10px] tracking-[0.4em] uppercase text-[#6a6a6a] mt-0">
            — Privacy Policy
          </p>
          <h1
            data-testid="privacy-title"
            className="font-display font-bold text-white uppercase mt-4 text-[10vw] sm:text-[6vw] md:text-[3.6vw] leading-[0.95]"
            style={{ letterSpacing: "0.18em" }}
          >
            Kept In Confidence
          </h1>
          <div className="w-16 h-px bg-white/60 mt-8" />
          <p className="font-serif-editorial italic text-[#c9c9c4] max-w-lg mt-6 leading-[1.7] text-[15px] md:text-[17px]">
            {cleanPolicyText("How we handle the trust you place in us — what we collect, why we hold it, and the rights that remain yours at all times.")}
          </p>
        </motion.div>
      </header>

      {/* Sections */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-0">
        <div className="border-t border-[#141414]">
          {SECTIONS.map((s, i) => (
            <motion.article
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8%" }}
              transition={{ duration: 0.9, delay: i * 0.03, ease }}
              className="grid grid-cols-12 gap-4 md:gap-8 py-7 md:py-10 border-b border-[#141414]"
              data-testid={`privacy-clause-${s.n}`}
            >
              <div className="col-span-12 md:col-span-2">
                <span className="font-mono-ui text-[11px] md:text-[12px] tracking-[0.3em] text-[#7a7a7a]">
                  § {s.n}
                </span>
              </div>
              <div className="col-span-12 md:col-span-10">
                <h2
                  className="font-display font-bold uppercase text-white text-[18px] md:text-[22px] leading-[1.2]"
                  style={{ letterSpacing: "0.16em" }}
                >
                  {s.title}
                </h2>
                <p className="font-serif-editorial text-[#c9c9c4] mt-4 leading-[1.85] text-[15px] md:text-[17px] max-w-2xl">
                  {cleanPolicyText(s.body)}
                </p>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-8%" }}
          transition={{ duration: 0.9, ease }}
          className="grid grid-cols-12 gap-4 md:gap-8 py-6 md:py-8"
        >
          <div className="col-span-12 md:col-span-2">
            <span className="font-mono-ui text-[11px] md:text-[12px] tracking-[0.3em] text-[#7a7a7a]">
              Contact
            </span>
          </div>
          <div className="col-span-12 md:col-span-10 space-y-3 font-mono-ui text-[11px] md:text-[12px] tracking-[0.25em] uppercase text-[#c9c9c4]">
            <p>
              Website:{" "}
              <a
                href="https://www.theblackhouse.in"
                className="text-white hover:underline underline-offset-4"
              >
                theblackhouse.in
              </a>
            </p>
            <p>
              Email:{" "}
              <a
                href="mailto:support@theblackhouse.in"
                className="text-white hover:underline underline-offset-4"
              >
                support@theblackhouse.in
              </a>
            </p>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#141414]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 md:py-5 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link
            data-testid="privacy-back-home"
            to="/"
            className="group inline-flex items-center gap-2 font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
          >
            <ArrowLeft
              size={13}
              strokeWidth={1.4}
              className="transition-transform duration-500 group-hover:-translate-x-1"
            />
            Return To The House
          </Link>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link
              to="/terms"
              className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
            >
              Terms & Conditions
            </Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link
              to="/accessibility"
              className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
            >
              Accessibility
            </Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link
              to="/security"
              className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
            >
              Security
            </Link>
            <span className="text-[#2a2a2a]">·</span>
            <a
              href="https://www.instagram.com/theblackhouse.tbh"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Instagram"
              className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500"
            >
              <Instagram
                size={13}
                strokeWidth={1.4}
                className="text-white/70 group-hover:text-white transition-colors duration-500"
              />
            </a>
            <a
              href="https://www.linkedin.com/company/the-black-house/"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="LinkedIn"
              className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500"
            >
              <Linkedin
                size={13}
                strokeWidth={1.4}
                className="text-white/70 group-hover:text-white transition-colors duration-500"
              />
            </a>
          </div>
        </div>
        <div className="border-t border-[#141414] px-6 md:px-10 py-3 flex items-center justify-between font-mono-ui text-[8px] md:text-[9px] tracking-[0.3em] uppercase text-[#3a3a3a]">
          <span>© MMXXVI — THE BLACK HOUSE</span>
          <span>All rights are gathered.</span>
        </div>
      </footer>
    </main>
  );
}
